import { useEffect, useRef, useState } from 'react';
import { Brush, Check, Crop, Loader2, RotateCcw, WandSparkles } from 'lucide-react';
import { useStudioImage } from '@/hooks/useStudioImage';

export const FULL_CROP = { x: 0, y: 0, w: 1, h: 1 };
const MIN = .08;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const HANDLES = ['nw', 'ne', 'sw', 'se'];

// Draft crop lives in the editor object as `cropDraft` until the customer applies it.
export const CropStage = ({ editor }) => {
  const o = editor.selected;
  const { url } = useStudioImage(o?.backgroundRemoved && o?.cutoutAssetId ? o.cutoutAssetId : o?.assetId);
  const frame = useRef(null), drag = useRef(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const crop = o?.cropDraft || o?.crop || FULL_CROP;
  useEffect(() => {
    const observer = new ResizeObserver(([entry]) => setBox({ w: entry.contentRect.width, h: entry.contentRect.height }));
    observer.observe(frame.current); return () => observer.disconnect();
  }, [url]);
  const ratio = o?.imageRatio || 1;
  const fit = box.w / box.h > ratio ? { w: box.h * ratio, h: box.h } : { w: box.w, h: box.w / ratio };
  const start = (e, mode) => {
    e.preventDefault(); e.stopPropagation();
    drag.current = { mode, startX: e.clientX, startY: e.clientY, crop: { ...crop }, pointerId: e.pointerId };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const move = e => {
    if (!drag.current || drag.current.pointerId !== e.pointerId) return;
    const { mode, startX, startY, crop: c } = drag.current;
    const dx = (e.clientX - startX) / fit.w, dy = (e.clientY - startY) / fit.h;
    let next;
    if (mode === 'move') next = { ...c, x: clamp(c.x + dx, 0, 1 - c.w), y: clamp(c.y + dy, 0, 1 - c.h) };
    else {
      let x1 = c.x, y1 = c.y, x2 = c.x + c.w, y2 = c.y + c.h;
      if (mode.includes('w')) x1 = clamp(x1 + dx, 0, x2 - MIN); else x2 = clamp(x2 + dx, x1 + MIN, 1);
      if (mode.includes('n')) y1 = clamp(y1 + dy, 0, y2 - MIN); else y2 = clamp(y2 + dy, y1 + MIN, 1);
      next = { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
    }
    editor.patchObject(o.id, { cropDraft: Object.fromEntries(Object.entries(next).map(([k, v]) => [k, Number(v.toFixed(4))])) }, false);
  };
  const end = () => { drag.current = null; };
  if (!o) return null;
  return <div className="cutout-stage crop-stage checkerboard" data-testid="crop-stage" aria-label="Bild zuschneiden">
    <div className="crop-frame" ref={frame}>
      {url ? <div className="crop-image-box" style={{ width: fit.w, height: fit.h }} onPointerMove={move} onPointerUp={end} onPointerCancel={end}>
        <img src={url} alt="" draggable={false} data-testid="crop-stage-image" />
        <div className="crop-shade" style={{ clipPath: `polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 ${crop.y * 100}%, ${crop.x * 100}% ${crop.y * 100}%, ${crop.x * 100}% ${(crop.y + crop.h) * 100}%, ${(crop.x + crop.w) * 100}% ${(crop.y + crop.h) * 100}%, ${(crop.x + crop.w) * 100}% ${crop.y * 100}%, 0 ${crop.y * 100}%)` }} />
        <div className="crop-rect" data-testid="crop-rect" style={{ left: `${crop.x * 100}%`, top: `${crop.y * 100}%`, width: `${crop.w * 100}%`, height: `${crop.h * 100}%` }} onPointerDown={e => start(e, 'move')}>
          {HANDLES.map(h => <span key={h} className={`crop-handle ${h}`} data-testid={`crop-handle-${h}`} onPointerDown={e => start(e, h)} />)}
        </div>
      </div> : <span className="cutout-loading" data-testid="crop-loading"><Loader2 size={22} className="spin" />Bild wird geladen …</span>}
    </div>
    <span className="cutout-stage-caption" data-testid="crop-stage-caption">{Math.round(crop.w * 100)} % × {Math.round(crop.h * 100)} %</span>
  </div>;
};

export const CropPanel = ({ editor, setPanel, processing }) => {
  const o = editor.selected;
  if (o?.type !== 'image') return <p data-testid="crop-empty">Wähle zuerst ein Bild aus.</p>;
  const crop = o.cropDraft || o.crop || FULL_CROP, full = crop.w >= .999 && crop.h >= .999;
  const job = processing?.jobs[o.id] || {}, fresh = !!o.prepare;
  const apply = () => {
    const ratio = (o.imageRatio || 1) * crop.w / crop.h;
    editor.patchObject(o.id, { crop, cropDraft: undefined, prepare: undefined, baseH: Number((o.baseW / ratio).toFixed(2)) });
    setPanel('object');
  };
  return <div className="cutout-panel" data-testid="crop-panel">
    <div className="cutout-status" data-testid="crop-status"><Crop size={20} /><div><strong>{fresh ? 'Dein Bild ist da – kurz vorbereiten?' : 'Nur der Ausschnitt, der zählt.'}</strong><p>Ziehe den Rahmen oder seine Ecken. Alles außerhalb wird nicht graviert.</p></div></div>
    <button className="preview-button cutout-wide-button" data-testid="crop-apply" disabled={o.locked || job.pending} onClick={apply}><Check size={18} />{fresh && full ? 'Weiter' : 'Ausschnitt übernehmen'}</button>
    <div className="cutout-secondary-actions"><button className="outline-button" data-testid="crop-reset" disabled={o.locked || full} onClick={() => editor.patchObject(o.id, { cropDraft: { ...FULL_CROP } }, false)}><RotateCcw size={16} />Ganzes Bild</button><button className="outline-button" data-testid="crop-cancel" onClick={() => { editor.patchObject(o.id, { cropDraft: undefined, prepare: undefined }, false); setPanel('object'); }}>{fresh ? 'Überspringen' : 'Abbrechen'}</button></div>
    <p className="property-footnote" data-testid="crop-note">Der Zuschnitt gilt für Original und Freistellung gleichermaßen. Dein hochgeladenes Bild bleibt unverändert.</p>
  </div>;
};
